"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { z } from "zod/v4";
import { useEvents } from "./pusher";
import { ProvisionUpdateSchema } from "skedyul-core/features/deployments/resources/types";
import { store, type RootState } from "@store/index";
import {
  getProvision,
  applyProvisionUpdate,
  clearProvision,
  addLog,
} from "@store/features/provisions/provisionsSlice";

const ProvisionLogEventSchema = z.object({
  provisionId: z.string().optional(),
  deploymentId: z.string().optional(),
  entries: z.array(
    z.object({
      timestamp: z.string(),
      level: z.enum(["info", "warn", "error", "debug"]),
      phase: z.enum(["build", "provision", "migration"]),
      type: z.enum(["log", "step", "approval_required", "approval_resolved"]),
      msg: z.string(),
      step: z
        .object({
          name: z.string(),
          status: z.enum([
            "pending",
            "in_progress",
            "awaiting_approval",
            "completed",
            "failed",
          ]),
          startedAt: z.string().optional(),
          completedAt: z.string().optional(),
          errorMessage: z.string().optional(),
        })
        .optional(),
    }),
  ),
});

/**
 * ProvisionsProvider handles Pusher subscriptions for provision updates.
 * It tracks which provisions are being watched and subscribes to their channels.
 */
export function ProvisionsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Track which provision IDs are being watched (with ref counts)
  const [tracked, setTracked] = useState<Map<string, number>>(() => new Map());
  const trackedIds = useMemo(() => Array.from(tracked.keys()), [tracked]);

  // Handle incoming Pusher events and dispatch to Redux
  const handleProvisionEvent = useCallback(
    (rawData: unknown) => {
      // eslint-disable-next-line no-console

      // Pusher wraps the payload in a "payload" property
      const env =
        typeof rawData === "object" && rawData !== null
          ? (rawData as Record<string, unknown>)
          : undefined;
      const payload = env && "payload" in env ? env["payload"] : rawData;

      const parsed = ProvisionUpdateSchema.safeParse(payload);
      if (!parsed.success) {
        // eslint-disable-next-line no-console
        console.warn(
          "[ProvisionsProvider] Failed to parse provision update:",
          parsed.error,
        );
        return;
      }

      const {
        provisionId,
        status,
        currentStep,
        steps,
        errorMessage,
        completedAt,
        pendingMigration,
      } = parsed.data;

      // eslint-disable-next-line no-console

      if (!provisionId || !tracked.has(provisionId)) {
        // eslint-disable-next-line no-console
        return;
      }

      // Dispatch to Redux slice
      // eslint-disable-next-line no-console
      store.dispatch(
        applyProvisionUpdate({
          provisionId,
          status,
          currentStep,
          steps,
          errorMessage,
          completedAt,
          pendingMigration,
        }),
      );
    },
    [tracked],
  );

  const handleProvisionLogEvent = useCallback(
    (rawData: unknown) => {
      const env =
        typeof rawData === "object" && rawData !== null
          ? (rawData as Record<string, unknown>)
          : undefined;
      const payload = env && "payload" in env ? env["payload"] : rawData;

      const parsed = ProvisionLogEventSchema.safeParse(payload);
      if (!parsed.success) return;

      const { provisionId, entries } = parsed.data;
      if (!provisionId || !tracked.has(provisionId)) {
        return;
      }

      if (store.getState().provisions.provision.data?.id !== provisionId) {
        return;
      }

      for (const entry of entries) {
        store.dispatch(
          addLog({
            id: `${entry.timestamp}-${entry.msg}`,
            timestamp: entry.timestamp,
            message: entry.msg,
            level:
              entry.level === "warn" || entry.level === "error"
                ? entry.level
                : "info",
            type: entry.type === "step" ? "step" : undefined,
            stepStatus: entry.step?.status,
          }),
        );
      }
    },
    [tracked],
  );

  // Create Pusher subscriptions for all tracked provisions
  const subscriptions = useMemo(() => {
    // eslint-disable-next-line no-console
    return trackedIds.map((provisionId) => ({
      channel: ({ subdomain }: { subdomain?: string | null }) => {
        const channelName = subdomain
          ? `private-${subdomain}-provision-${provisionId}`
          : "";
        // eslint-disable-next-line no-console
        return channelName;
      },
      events: {
        "provision.updated": handleProvisionEvent,
        "provision.log": handleProvisionLogEvent,
      },
    }));
  }, [trackedIds, handleProvisionEvent, handleProvisionLogEvent]);

  useEvents(subscriptions);

  // Expose tracking functions via context
  const contextValue = useMemo(
    () => ({
      trackProvision: (provisionId: string) => {
        if (!provisionId) return;
        setTracked((prev) => {
          const next = new Map(prev);
          const current = next.get(provisionId) ?? 0;
          next.set(provisionId, current + 1);
          return next;
        });
      },
      untrackProvision: (provisionId: string) => {
        if (!provisionId) return;
        setTracked((prev) => {
          const current = prev.get(provisionId);
          if (typeof current !== "number") return prev;
          const next = new Map(prev);
          if (current <= 1) {
            next.delete(provisionId);
          } else {
            next.set(provisionId, current - 1);
          }
          return next;
        });
      },
    }),
    [],
  );

  return (
    <ProvisionsContext.Provider value={contextValue}>
      {children}
    </ProvisionsContext.Provider>
  );
}

// Context for tracking functions only
type ProvisionsContextValue = {
  trackProvision: (provisionId: string) => void;
  untrackProvision: (provisionId: string) => void;
};

const ProvisionsContext = React.createContext<ProvisionsContextValue>({
  trackProvision: () => {},
  untrackProvision: () => {},
});

export function useProvisionsContext(): ProvisionsContextValue {
  return React.useContext(ProvisionsContext);
}

/**
 * Hook to subscribe to a provision and get its data from Redux.
 * Handles tracking for Pusher subscriptions and fetches initial data.
 */
export function useProvisionRealtime(provisionId?: string | null) {
  const { trackProvision, untrackProvision } = useProvisionsContext();

  // Select provision data from Redux
  const provision = useSelector(
    (state: RootState) => state.provisions.provision.data,
  );
  const isLoading = useSelector(
    (state: RootState) => state.provisions.provision.isLoading,
  );
  const error = useSelector(
    (state: RootState) => state.provisions.provision.error,
  );
  const pendingMigration = useSelector(
    (state: RootState) => state.provisions.pendingMigration,
  );
  const logs = useSelector((state: RootState) => state.provisions.logs);

  // Track provision for Pusher subscription
  useEffect(() => {
    if (!provisionId) return;
    trackProvision(provisionId);
    return () => {
      untrackProvision(provisionId);
    };
  }, [provisionId, trackProvision, untrackProvision]);

  // Fetch provision data when provisionId changes
  useEffect(() => {
    if (!provisionId) return;

    // Only fetch if we don't have the data or it's a different provision
    if (!provision || provision.id !== provisionId) {
      void store.dispatch(getProvision({ provisionId }));
    }
  }, [provisionId, provision]);

  // Clear provision when unmounting or provisionId changes
  useEffect(() => {
    return () => {
      store.dispatch(clearProvision());
    };
  }, [provisionId]);

  // Only return data if it matches the requested provisionId
  const matchesCurrentId = provision?.id === provisionId;

  return {
    provision: matchesCurrentId ? provision : null,
    isLoading,
    error,
    pendingMigration: matchesCurrentId ? pendingMigration : null,
    logs: matchesCurrentId ? logs : [],
  };
}

/**
 * Hook to stream provision updates for the sidebar.
 * This is a simpler version that just ensures the provision is tracked
 * and fetched - the sidebar reads directly from Redux state.
 *
 * Includes fallback polling for when Pusher isn't available (local dev).
 */
export function useStreamProvisionUpdates(
  provisionId: string | undefined,
): void {
  const { trackProvision, untrackProvision } = useProvisionsContext();

  // Track provision for Pusher subscription
  useEffect(() => {
    if (!provisionId) return;
    trackProvision(provisionId);
    return () => {
      untrackProvision(provisionId);
    };
  }, [provisionId, trackProvision, untrackProvision]);

  // Fetch provision data when provisionId changes
  useEffect(() => {
    if (!provisionId) return;
    void store.dispatch(getProvision({ provisionId }));
  }, [provisionId]);

  // Fallback polling for local development (when Pusher might not be configured)
  // Poll every 2 seconds while the provision is not completed/failed
  useEffect(() => {
    if (!provisionId) return;

    const pollInterval = setInterval(() => {
      const currentProvision = store.getState().provisions.provision.data;

      // Only poll if we have a provision and it's not in a terminal state
      if (
        currentProvision?.id === provisionId &&
        currentProvision.status !== "COMPLETED" &&
        currentProvision.status !== "FAILED"
      ) {
        // eslint-disable-next-line no-console
        void store.dispatch(getProvision({ provisionId }));
      }
    }, 2000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [provisionId]);
}
